import { theme as antdTheme, type ThemeConfig } from 'antd';
import { normalizeUiDensity } from '../services/ports/uiDensityPreference';
import { getSystemDensityMetrics } from './uiDensity';

// Use the existing palette and its resolved sizes, including portal components.
export function getUiDensityTheme(
  base: ThemeConfig,
  density: number,
): ThemeConfig {
  if (normalizeUiDensity(density) === 100) return base;
  const token = antdTheme.getDesignToken(base);
  const { spaceScale, sizeScale, fontScale } = getSystemDensityMetrics(density);
  const space = (value: number) => value * spaceScale;
  const size = (value: number) => value * sizeScale;
  const font = (value: number) =>
    Math.max(Math.min(value, 10), value * fontScale);
  const scaledTokens = Object.fromEntries(
    Object.entries(token).flatMap(([key, value]) => {
      if (typeof value !== 'number') return [];
      if (/^(padding|margin|controlPaddingHorizontal)/.test(key))
        return [[key, space(value)]];
      if (/^fontSize/.test(key)) return [[key, font(value)]];
      if (/^controlHeight/.test(key)) return [[key, Math.max(20, size(value))]];
      return [];
    }),
  );
  const components = base.components;
  return {
    ...base,
    token: { ...base.token, ...scaledTokens },
    components: {
      ...components,
      Table: {
        ...components?.Table,
        cellPaddingBlock: space(token.padding),
        cellPaddingInline: space(token.padding),
        cellPaddingBlockMD: space(token.paddingSM),
        cellPaddingInlineMD: space(token.paddingXS),
        cellPaddingBlockSM: space(token.paddingXS),
        cellPaddingInlineSM: space(token.paddingXS),
      },
      Form: {
        ...components?.Form,
        itemMarginBottom: space(token.marginLG),
        verticalLabelPadding: `0 0 ${space(token.paddingXS)}px`,
      },
      Card: {
        ...components?.Card,
        headerHeight: size(56),
        headerHeightSM: size(38),
        bodyPadding: space(token.paddingLG),
        bodyPaddingSM: space(token.paddingSM),
      },
      List: {
        ...components?.List,
        itemPadding: `${space(token.paddingSM)}px 0`,
        itemPaddingSM: `${space(token.paddingXS)}px ${space(token.padding)}px`,
        itemPaddingLG: `${space(token.padding)}px ${space(token.paddingLG)}px`,
      },
      Menu: { ...components?.Menu, itemHeight: size(40) },
      Select: {
        ...components?.Select,
        optionHeight: Math.max(24, size(32)),
        optionPadding: `${space(5)}px ${space(token.paddingSM)}px`,
      },
      Tabs: {
        ...components?.Tabs,
        horizontalItemGutter: space(token.marginLG + token.marginXS),
        horizontalItemPadding: `${space(token.paddingSM)}px 0`,
        horizontalItemPaddingSM: `${space(token.paddingXS)}px 0`,
        horizontalItemPaddingLG: `${space(token.padding)}px 0`,
        verticalItemPadding: `${space(token.paddingXS)}px ${space(token.paddingLG)}px`,
      },
    },
  };
}
